// Sidebar navigation extracted from Shell.tsx.

import type { JSX } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth.js";
import { apiFetch } from "../lib/api.js";

export interface TenantConfigResponse { name: string; module_config: Record<string, boolean>; }

// module key = undefined means "always visible"; roles = undefined means all roles
export const NAV: { to: string; label: string; module?: string; roles?: string[] }[] = [
  { to: "/dashboard",         label: "Dashboard" },
  { to: "/patients",          label: "Patients" },
  { to: "/sr",                label: "Service Requests" },
  { to: "/carers",            label: "Carers" },
  { to: "/agents",            label: "Agents" },
  { to: "/agencies",          label: "Agencies",          roles: ["coordinator", "agency_admin"] },
  { to: "/health-passport",   label: "Health Passport",   module: "health_passport" },
  { to: "/device-kits",       label: "Device Kits",      module: "device_kits" },
  { to: "/performance",       label: "Performance",       module: "performance" },
  { to: "/analytics",         label: "Analytics",         module: "analytics" },
  { to: "/billing",           label: "Billing",           module: "billing" },
  { to: "/settings/payments", label: "Payment Settings",  module: "billing",            roles: ["agency_admin"] },
  { to: "/automation",        label: "Automation",        module: "automation" },
  { to: "/nsofar-sync",       label: "Nsofar Sync",       module: "nsofar_sync",        roles: ["super_admin", "agency_admin"] },
  { to: "/conflicts",         label: "Conflicts",         module: "conflict_resolution", roles: ["super_admin", "agency_admin"] },
  // Q7 D13 13a — module keys aligned to canonical service_type vocabulary
  // (TASK-44 closed those keys in tenant config). PM-JAY removed pending a
  // proper PMJAY workflow that introduces a dedicated module key.
  // nsofar doctor-consult ops queue (docs/33) — module key follows the
  // canonical service_type vocabulary like the entries below.
  { to: "/consult-ops",                   label: "Consult Ops",           module: "tele_consult" },
  { to: "/modules/nutritionist",          label: "Nutritionist",          module: "nutritionist_call" },
  { to: "/modules/diagnostics",           label: "Diagnostics",           module: "diagnostics_visit" },
  { to: "/modules/medicine-delivery",     label: "Medicine Delivery",     module: "medicine_delivery" },
  { to: "/modules/property-management",   label: "Property Management",   module: "property_visit" },
  { to: "/call-log",          label: "Call Log",          module: "call_log" },
  { to: "/audit-logs",        label: "Audit Log",                                       roles: ["super_admin", "agency_admin"] },
  { to: "/users",             label: "Users",                                           roles: ["super_admin", "agency_admin"] },
];

export function useVisibleNav(): typeof NAV {
  const { user } = useAuth();
  const { data } = useQuery<TenantConfigResponse>({
    queryKey: ["tenant-config"],
    queryFn: () => apiFetch<TenantConfigResponse>("/tenants/me/config"),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });
  const cfg = data?.module_config ?? {};
  return NAV.filter((n) => {
    if (n.module !== undefined && cfg[n.module] === false) return false;
    if (n.roles !== undefined && !n.roles.includes(user?.role ?? "")) return false;
    return true;
  });
}

function SidebarLink({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-navy-50 text-navy-800"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export function Sidebar(): JSX.Element {
  const nav = useVisibleNav();
  return (
    <aside className="w-56 shrink-0 bg-white border-r border-slate-200 hidden md:flex flex-col">
      <div className="px-4 py-5 border-b border-slate-100">
        <span className="text-lg font-bold text-navy-700">HCM</span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => <SidebarLink key={n.to} to={n.to} label={n.label} />)}
      </nav>
      <div className="px-4 py-4 border-t border-slate-100">
        <a
          href="https://nsofar.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Powered by Nsofar
        </a>
      </div>
    </aside>
  );
}
